import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useExercisesHomework, HomeworkMessage } from './useExercisesHomework';
import BotMessage from '../../components/v2/bot-message';
import { LessonActionBtn } from '../../components/v2/action-buttons';
import ChatInputTool from '../../components/v2/chat-input-tool';
import UserMessage from '../../components/v2/user-message';
import { arrowFullscreen, captureExerciseIcon } from '../../assets/images';
import ZoomChartModal from '../../components/zoomable-image';
import { useUserStore } from '../../utils/userStore';
import MathExpressionNew from '../../components/common/mathjax';
import { hebrewLetters } from '../../configs/config';
import { useLayoutContext } from '../../layouts/general-layout';
import QRCode from 'react-qr-code';
import CheckIcon from '../../assets/images/check.svg?react';
import ReactCrop, { Crop, PixelCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Button from '../../components/button';
import ChatHeader from '../../components/chat-header';
import { Loader } from '../../assets/images';
import './exercises-homework.scss';
import '../capture-exercise/capture-exercise.scss';

const shouldShowQuestionText = (text: string, letter: string, sectionTranslation: string) => {
  if (!text) return false;
  const trimmed = text.trim().replace(/['"׳`״]/g, '');
  const cleanLetter = letter.trim().replace(/['"׳`״]/g, '');
  if (trimmed === cleanLetter) return false;
  if (trimmed === `${sectionTranslation} ${cleanLetter}`) return false;
  if (trimmed === `${cleanLetter}.`) return false;
  if (trimmed.length <= 3) return false;
  return true;
};

const ExercisesHomework: React.FC = () => {
  const { t } = useTranslation();
  const { openModesModal } = useLayoutContext();
  const {
    messages,
    isTyping,
    onMessageComplete,
    completedMessageIds,
    exerciseQuestions,
    selectedQuestionId,
    selectQuestionFromList,
    addUserMessage,
    solvedQuestionIds,
    selectedQuestionIds,
    startManualCapture,
    isMobileActive
  } = useExercisesHomework();

  const { userInfo } = useUserStore();
  const [zoomImageSrc, setZoomImageSrc] = React.useState<string | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const userInitials = userInfo?.first_name?.charAt(0) || 'U';


  useEffect(() => {
    const container = chatContainerRef.current;
    const scrollContent = scrollContentRef.current;

    if (container && scrollContent) {
      const observer = new ResizeObserver(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });

      observer.observe(scrollContent);
      return () => observer.disconnect();
    }
  }, []);

  useEffect(() => {
    messages.forEach(msg => {
      if (
        ((msg.type === 'captured_image' || msg.type === 'questions_list') ||
         (msg.type === 'options' && !msg.text)) &&
        !completedMessageIds.has(msg.id)
      ) {
        onMessageComplete(msg.id);
      }
    });
  }, [messages, completedMessageIds, onMessageComplete]);

  const handleChartClick = (imgSrc: string) => {
    setZoomImageSrc(imgSrc);
  };

  const getIcon = (iconKey: React.ReactNode) => {
    if (iconKey === 'capture') {
      return <img src={captureExerciseIcon} alt="" className="capture-btn-icon" />;
    }
    return iconKey;
  };

  const renderMessage = (msg: HomeworkMessage) => {
    if (msg.sender === 'user') {
      return <UserMessage key={msg.id} text={msg.text} initials={userInitials} />;
    }

    if (msg.type === 'qr_code') {
      return (
        <div key={msg.id} className="solution-capture-qr-container fade-in">
          <div className="solution-capture-qr-card">
            <h6 className="qr-card-title">{t("homeworkCaptureQRTitle")}</h6>
            <p className="qr-card-subtitle">{t("homeworkCaptureQRSubtitle")}</p>
            
            <div className="qr-code-wrapper">
              {msg.qrCodeUrl ? (
                <QRCode value={msg.qrCodeUrl} size={200} />
              ) : (
                <div className="qr-code-spinner-container">
                  <img src={Loader} alt="Loader" className="qr-loader-spinner" />
                </div>
              )}
              {isMobileActive && (
                <div className="qr-code-loader-overlay">
                  <img src={Loader} alt="Loader" className="qr-loader-spinner" />
                  <span>{t("solutionCaptureMobileConnected")}</span>
                </div>
              )}
            </div>

            <div className="qr-card-actions">
              <LessonActionBtn
                text={t("solutionCaptureUploadFromPC")}
                onClick={startManualCapture}
                color="dark"
                variant="outlined"
              />
            </div>
          </div>
        </div>
      );
    }

    if (msg.type === 'captured_image') {
      return (
        <div key={msg.id} className="fade-in chat-image-block" onClick={() => handleChartClick(msg.imageUrl!)}>
          <img src={arrowFullscreen} alt={t('enlarge')} className="fullscreen-icon" />
          <div className="chat-image-wrapper">
            <img src={msg.imageUrl} alt={t('capturedExerciseAlt')} className="captured-exercise-img" />
          </div>
        </div>
      );
    }

    if (msg.type === 'questions_list') {
      return (
        <div key={msg.id} className="fade-in questions-bubble-card">
          <div className="questions-card-container">
            {exerciseQuestions.map((q, idx) => {
              const isSelected = selectedQuestionId === q.id || selectedQuestionIds?.has(q.id);
              const isSolved = solvedQuestionIds?.has(q.id);
              const letter = hebrewLetters[idx] || String(idx + 1);
              return (
                <div
                  key={q.id}
                  className={`question-item-card ${isSelected ? 'active' : ''} ${isSolved ? 'solved' : ''}`}
                  onClick={() => selectQuestionFromList(q, letter)}
                >
                  <div className="question-left-area">
                    <div className="question-title-text">
                      {t('section')} {letter} {t('forSolution')}
                    </div>
                    {shouldShowQuestionText(q.question, letter, t('section')) && (
                      <div className="question-content-text">
                        <MathExpressionNew content={q.question} />
                      </div>
                    )}
                  </div>
                  <div className="question-status-container">
                    {(isSolved || isSelected) ? (
                      <div className="status-checkmark-wrapper">
                        <CheckIcon className="check-icon" />
                      </div>
                    ) : (
                      <div className="status-hollow-circle" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (msg.type === 'options' && !msg.text) {
      return (
        <div key={msg.id} className="exercises-homework-page__actions fade-in exercises-homework-page__actions--margin-top">
          {msg.options?.map((opt, i) => (
            <LessonActionBtn
              key={i}
              onClick={opt.action!}
              text={opt.label}
              color={opt.color || "black"}
              variant={opt.variant || "contained"}
              icon={getIcon(opt.icon)}
            />
          ))}
        </div>
      );
    }

    const isCompleted = completedMessageIds.has(msg.id);
    return (
      <div key={msg.id} className="bot-message-wrapper">
        <BotMessage
          text={msg.text}
          isLoading={msg.type === 'loader_bubble'}
          loadingText={msg.loadingText}
          onComplete={() => onMessageComplete(msg.id)}
        />
        {isCompleted && msg.options && (
          <div className="exercises-homework-page__actions fade-in">
            {msg.options.map((opt, i) => (
              <LessonActionBtn
                key={i}
                onClick={opt.action!}
                text={opt.label}
                color={opt.color || "black"}
                variant={opt.variant || "contained"}
                icon={getIcon(opt.icon)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="exercises-homework-page">
      <main className="exercises-homework-page__chat" ref={chatContainerRef}>
        <div className="exercises-homework-page__scroll-wrapper" ref={scrollContentRef}>
          {messages.map(renderMessage)}
          {isTyping && <BotMessage isLoading playAudio={false} />}
          <div className="exercises-homework-page__bottom-spacer" />
        </div>
      </main>

      <ChatInputTool
        onSendMessage={addUserMessage}
        onMicClick={() => {}}
        onAttachClick={startManualCapture}
        onStudyModeClick={openModesModal}
        placeholder={t('exercisePracticePlaceholder')}
      />

      {zoomImageSrc && <ZoomChartModal imageSrc={zoomImageSrc} onClose={() => setZoomImageSrc(null)} />}
    </div>
  );
};

export default ExercisesHomework;
